/**
 * Coach Panel copy — G4D CP2.
 *
 * Every user-facing string exported from this file is banned-phrase-
 * swept in `__tests__/coach_panel.test.js` using the exact same
 * vocabulary as `quality_copy.test.js`. If a future edit accidentally
 * introduces "guaranteed" / "coming soon" / "you qualify" etc, the
 * sweep test fails BEFORE the copy ships.
 *
 * The mandatory disclaimer text is snapshot-locked so any accidental
 * word drift blocks CI.
 */

// Snapshot-locked required disclaimer. Do NOT edit without an
// explicit user brief.
export const COACH_DISCLAIMER =
  "The Coach provides draft-improvement guidance based on your " +
  "proposal content. It does not predict funding decisions or " +
  "guarantee outcomes.";

// Header copy above the panel.
export const COACH_PANEL_HEADING = "AI Grant Coach";
export const COACH_PANEL_KICKER = "Grant Writer · Coach";
export const COACH_PANEL_SUBHEADING =
  "Get grounded, draft-improvement guidance for this proposal.";

// Locked state (proposal has zero generated sections).
export const COACH_LOCKED_TITLE = "Coach unlocks once a section is generated";
export const COACH_LOCKED_BODY =
  "Generate at least one proposal section to unlock AI Grant Coach " +
  "recommendations. The Coach reads your drafted sections to give " +
  "grounded, section-specific guidance.";
export const COACH_LOCKED_CTA = "Back to Generate step";

// Empty conversation welcome state.
export const COACH_EMPTY_TITLE = "Ask the Coach";
export const COACH_EMPTY_BODY =
  "Start by asking about a specific section, a weak-score dimension, " +
  "or reviewer expectations for your grant. The Coach reads your " +
  "current draft to reply with targeted, non-award-predictive advice.";

// Counter template (free tier only). {n} substituted with the
// remaining count. The full sentence is rendered verbatim (no
// pluralisation / no localisation).
export const COACH_COUNTER_LABEL = "Free Coach messages remaining";
export const COACH_COUNTER_TOTAL = 3;

// Kill-switch / fallback banner. Rendered as a neutral gray strip
// above the coach reply bubble whenever `prompt_version` equals
// the fallback marker OR the panel notices a global kill-switch.
export const COACH_KILL_SWITCH_NOTICE =
  "Live Coach guidance temporarily unavailable — draft-level scoring " +
  "above remains active.";

// Rate-limit inline banner (premium). `{n}` will be replaced with
// the `retry_after_hint` seconds count. The rest of the sentence
// is fixed.
export const COACH_RATE_LIMIT_PREFIX =
  "Slow down — you're sending Coach messages faster than usual. Try again in ";
export const COACH_RATE_LIMIT_SUFFIX = "s.";

// Chip / action labels.
export const COACH_ACTIONS_HEADING = "Recommended Next Actions";
export const COACH_ACTION_INSERT_LABEL = "Insert Suggestion";
export const COACH_ACTION_INSERT_DISABLED_TOOLTIP =
  "Available in next release";
export const COACH_ACTION_ASK_FOLLOWUP_LABEL = "Ask Follow-up";
export const COACH_ACTION_DISMISS_LABEL = "Dismiss";
export const COACH_ACTION_PREVIEW_LABEL = "Preview suggestion";

// Input placeholder + send button.
export const COACH_INPUT_PLACEHOLDER =
  "Ask about a specific section, a weakness, or reviewer expectations…";
export const COACH_SEND_LABEL = "Send";
export const COACH_INPUT_MAX_CHARS = 2000;
export const COACH_INPUT_WARN_AT = 1800;

// Retry-on-error affordance.
export const COACH_RETRY_LABEL = "Retry";
export const COACH_ERROR_HINT =
  "That message didn't reach the Coach. Retry when you're ready.";

// Load-error copy.
export const COACH_LOAD_ERROR =
  "The Coach conversation couldn't be loaded. Refresh the page or " +
  "come back in a moment.";


// ---------------------------------------------------------------------------
// G4D CP3 — Insert Suggestion modal + Undo toast copy.
// Every string here is banned-phrase-swept by insert_suggestion.test.js.
// ---------------------------------------------------------------------------

export const INSERT_MODAL_KICKER = "Grant Writer · Insert Suggestion";
export const INSERT_MODAL_HEADER_PREFIX = "Insert Coach suggestion into:";
export const INSERT_MODAL_RATIONALE_LABEL = "Coach's reason:";
export const INSERT_APPEND_LABEL = "Append";
export const INSERT_REPLACE_LABEL = "Replace";
export const INSERT_MODE_DESCRIPTION_APPEND =
  "Add the suggestion to the end of the current section content. Default option — safer for iterative editing.";
export const INSERT_MODE_DESCRIPTION_REPLACE =
  "Substitute the section content with the suggestion. Existing content will be discarded from the section (Undo restores it).";
export const INSERT_PREVIEW_CURRENT_LABEL = "Current section";
export const INSERT_PREVIEW_AFTER_LABEL = "After insert";
export const INSERT_LOADING_SECTION = "Loading current section content…";
export const INSERT_SUBMIT_LABEL = "Insert";
export const INSERT_SUBMITTING_LABEL = "Inserting…";
export const INSERT_CANCEL_LABEL = "Cancel";
export const INSERT_REFRESH_DIFF_LABEL = "Refresh diff";

export const INSERT_SECTION_CHANGED_MESSAGE =
  "The section was edited since this suggestion was generated. Review the current section before inserting.";
export const INSERT_GUARDRAIL_ERROR =
  "This suggestion no longer passes our content guidelines. Please regenerate the Coach reply.";
export const INSERT_ERROR_HINT =
  "The insert couldn't be applied. Try again in a moment or refresh the diff.";

// Undo toast.
export const INSERT_UNDO_TOAST_PREFIX = "Coach suggestion inserted into ";
export const INSERT_UNDO_TOAST_SUFFIX = ". Undo insert to restore the previous section content.";
export const INSERT_UNDO_LABEL = "Undo insert";
export const INSERT_UNDO_VIEW_SECTION_LABEL = "View updated section";
export const INSERT_UNDO_CONFIRMATION = "Insert undone. Previous section content restored.";
export const INSERT_UNDO_EXPIRED = "Undo period expired.";
export const INSERT_UNDO_ALREADY_UNDONE = "This insert has already been undone.";
export const INSERT_UNDO_SECTION_MODIFIED =
  "Section was edited after insert — undo unavailable.";
